import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Zap, Target, TrendingUp, Eye, MessageCircle, Lock } from 'lucide-react';
export function PSIProBenefitsSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-5 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-2532" data-mp="37e44nu2i0l02">
      {/* Mystical background effects - matching PSIGatheringSection */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-2533" data-mp="3h4f9diu0ay80" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-2534" data-mp="20qyx857hsr6p" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-2535" data-mp="1vtyrjvek1qg4" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-2536" data-mp="1w1h1vdlhixx8">
        {/* Main headline */}
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif text-center mb-10 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-id="element-2537" data-mp="29sm48ng8m4b9">
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-2538" data-mp="2jruvv9nmr6ug">
            PSI PRO
          </span>{' '}
          Is Where The Paradigm Shift Happens
        </h2>

        <p className="text-xl text-stone-300 text-center mb-16" data-id="element-2539" data-mp="tevoukjo3tmb">
          Here's what you're actually getting when you join:
        </p>

        {/* Benefits List */}
        <div className="space-y-16" data-id="element-2540" data-mp="1rqgfm5m310in">
          {/* Benefit 1: Paradigm Shift */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-2541" data-mp="25ppwz8m0y0o7">
            <div className="absolute inset-0 bg-gradient-radial from-purple-600/10 via-transparent to-transparent blur-2xl" data-id="element-2542" data-mp="2fmayvl2qyvnt" />

            <div className="relative bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-5 sm:p-8 md:p-10 rounded-lg" data-id="element-2543" data-mp="2amydz5vfrynv">
              <div className="flex items-start gap-4 mb-6" data-id="element-2544" data-mp="2kfncz19svk2b">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center" data-id="element-2545" data-mp="2yedbmlc9prss">
                  <Brain className="w-6 h-6 text-black" data-id="element-2546" data-mp="2tfskdbezuigh" />
                </div>
                <div className="flex-1" data-id="element-2547" data-mp="3d12jbagobba9">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2548" data-mp="3d23t7blydrqe">
                    <span className="text-green-400 text-2xl" data-id="element-2549" data-mp="3hzwzs5r64nr7">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-purple-400" data-id="element-2550" data-mp="3hy2olj5p8a0b">
                      THE MOST IMPORTANT PARADIGM SHIFT YOU'LL EVER MAKE ABOUT
                      PEPTIDES
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-2551" data-mp="2u99r3i9fhu3l">
                <p className="text-stone-100 font-semibold" data-id="element-2552" data-mp="2z2vsgea56bk3">
                  This is the shift that changes everything.
                </p>

                <div className="space-y-2 pl-6 border-l-2 border-purple-500/30" data-id="element-2553" data-mp="2pbhr00ymr1ob">
                  <p data-id="element-2554" data-mp="2p9591nsvgrb9">
                    The shift that allows you to see what's ACTUALLY possible.
                  </p>
                  <p data-id="element-2555" data-mp="2gajstzavhodn">
                    The shift that separates the masters from everyone else.
                  </p>
                </div>

                <p className="text-amber-400 font-bold" data-id="element-2556" data-mp="288sql1b4f9qj">
                  We've NEVER seen this covered by anyone else online. Ever.
                </p>

                <p data-id="element-2557" data-mp="1yk2g1120mdfx">
                  And it's something Brenden and Ali will reveal during the live
                  strategic briefings.
                </p>

                <p className="italic text-stone-400" data-id="element-2558" data-mp="1uigvctj8ouv6">
                  I can't tell you what it is on this page.
                </p>

                <p className="text-stone-400" data-id="element-2559" data-mp="1kksy9wy5vb9u">
                  (Because frankly, if I did, you'd think I was making it up.)
                </p>

                <div className="bg-purple-950/30 border-l-4 border-purple-500 p-6 my-6" data-id="element-2560" data-mp="1flyh0zfuduru">
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-2561" data-mp="1bl66m2v8hjtz">
                    But once you see it...
                  </p>
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-2562" data-mp="39x4unqkk0sxg">
                    Once you UNDERSTAND it...
                  </p>
                  <p className="text-xl text-purple-400 font-bold" data-id="element-2563" data-mp="rqwh4e169xok">
                    Everything else clicks into place.
                  </p>
                </div>

                <div className="space-y-2" data-id="element-2564" data-mp="wmv35uz133lt">
                  <p data-id="element-2565" data-mp="11gz9qd4dt9md">Your entire approach to biology changes.</p>
                  <p className="text-stone-100 font-semibold" data-id="element-2566" data-mp="15h9pghhz7eef">
                    And you'll wonder how you ever operated without knowing
                    this.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 2: The Impossible */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="relative" data-id="element-2567" data-mp="hrfek5h7vzwt">
            <div className="absolute inset-0 bg-gradient-radial from-cyan-600/10 via-transparent to-transparent blur-2xl" data-id="element-2568" data-mp="1y66tpgpcrao6" />

            <div className="relative bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-5 sm:p-8 md:p-10 rounded-lg" data-id="element-2569" data-mp="3u8k20l01xlot">
              <div className="flex items-start gap-4 mb-6" data-id="element-2570" data-mp="3q7gnq2xnjxpp">
                <div className="flex-shrink-0 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center" data-id="element-2571" data-mp="82d99rlsq9cm">
                  <Zap className="w-6 h-6 text-black" data-id="element-2572" data-mp="34btbfjo2q5d" />
                </div>
                <div className="flex-1" data-id="element-2573" data-mp="3bi2cepain67o">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2574" data-mp="3bj3maqfspmnt">
                    <span className="text-green-400 text-2xl" data-id="element-2575" data-mp="36ksbacdrl640">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-cyan-400" data-id="element-2576" data-mp="3l8dgqcy43hf3">
                      HOW TO DO THE "IMPOSSIBLE" WITH HUMAN BIOLOGY
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-2577" data-mp="2sr1d205hh8nl">
                <p data-id="element-2578" data-mp="2ns7wfyuwh1j7">
                  This is where Brenden and Ali show you what's REALLY possible
                  when you understand peptides at the highest level.
                </p>

                <div className="space-y-2 pl-6 border-l-2 border-cyan-500/30" data-id="element-2579" data-mp="182yz6y814bal">
                  <p data-id="element-2580" data-mp="ub2az59heljh">Not just "optimization."</p>
                  <p data-id="element-2581" data-mp="ucddl1r6xn90">Not just "improvement."</p>
                </div>

                <p className="text-xl text-cyan-400 font-bold" data-id="element-2582" data-mp="yd5os0jmi27x">
                  But actual REGENERATION of tissues that mainstream medicine
                  considers "impossible to heal."
                </p>

                <div className="bg-cyan-950/30 p-6 rounded-lg space-y-3" data-id="element-2583" data-mp="1qzi3993z790a">
                  <p className="text-stone-100" data-id="element-2584" data-mp="1d1tgfvqclrsl">
                    <span className="text-cyan-400 font-bold" data-id="element-2585" data-mp="1d34k7vjsmzjp">REVERSAL</span> of
                    conditions that doctors say are "permanent."
                  </p>
                  <p className="text-stone-100" data-id="element-2586" data-mp="1l22kwfruqbv">
                    <span className="text-cyan-400 font-bold" data-id="element-2587" data-mp="6ky6ywsdsjry">RESTORATION</span>{' '}
                    of function that specialists say "can't be done."
                  </p>
                </div>

                <div className="space-y-2" data-id="element-2588" data-mp="t9fx0k02b29i">
                  <p className="text-stone-100 font-semibold" data-id="element-2589" data-mp="t8d4y8r06ts0">
                    You'll see real case studies.
                  </p>
                  <p className="text-stone-100 font-semibold" data-id="element-2590" data-mp="ocfrca74k1ix">
                    Real transformations.
                  </p>
                  <p className="text-stone-100 font-semibold" data-id="element-2591" data-mp="1gyr22dog3itg">
                    Real "impossible" results.
                  </p>
                </div>

                <p className="text-xl text-cyan-400 font-bold" data-id="element-2592" data-mp="1c0fr1zmez29n">
                  And you'll learn the EXACT frameworks they used to create
                  them.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Benefit 3: Precision Cell™ Pro */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-2593" data-mp="1zqa0s8bfsapo">
            <div className="absolute inset-0 bg-gradient-radial from-emerald-600/10 via-transparent to-transparent blur-2xl" data-id="element-2594" data-mp="27a2l2yb6dxj9" />

            <div className="relative bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-5 sm:p-8 md:p-10 rounded-lg" data-id="element-2595" data-mp="2c6sytezich1i">
              <div className="flex items-start gap-4 mb-6" data-id="element-2596" data-mp="2l9c0hvrk4nm6">
                <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center" data-id="element-2597" data-mp="2l6pui0kbeg62">
                  <Target className="w-6 h-6 text-black" data-id="element-2598" data-mp="2uyls7aiho49r" />
                </div>
                <div className="flex-1" data-id="element-2599" data-mp="2v0or6k1md7qm">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2600" data-mp="2zy84nhqhtghi">
                    <span className="text-green-400 text-2xl" data-id="element-2601" data-mp="2zzj7nfc46k7k">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-emerald-400" data-id="element-2602" data-mp="34ycq7m69d1el">
                      THE PRECISION CELL™ PRO METHODOLOGY
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-2603" data-mp="o5fjf409wmpd">
                <p data-id="element-2604" data-mp="t40gwvo54sa4">
                  This is the same diagnostic and protocol design framework
                  Brenden and Ali use with private clients paying $15K−$120K.
                </p>

                <p className="text-emerald-400 font-bold" data-id="element-2605" data-mp="12y9557651z2h">
                  You're getting access to it as part of your PSI PRO
                  membership.
                </p>

                <p className="text-stone-100 font-semibold" data-id="element-2606" data-mp="1bv9uph4y237i">
                  Which means you'll learn:
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-emerald-500/30" data-id="element-2607" data-mp="1gu4n9aspr64t">
                  <p className="flex items-start gap-3" data-id="element-2608" data-mp="1lsxzz106et3x">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-2609" data-mp="3io3rjc7f4n80">→</span>
                    <span data-id="element-2610" data-mp="3nm5bdzco4qkj">
                      How to think diagnostically instead of symptomatically
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-2611" data-mp="3g476y99cg5o6">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-2612" data-mp="3bbmjdph16ot3">→</span>
                    <span data-id="element-2613" data-mp="36d2ubg754wwi">
                      How to identify root causes instead of just treating
                      surface issues
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-2614" data-mp="31c5cx5tyg6w2">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-2615" data-mp="2v1g8pb2ypxw">→</span>
                    <span data-id="element-2616" data-mp="3u2dwn2452zcr">
                      How to design protocols based on molecular mechanisms
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-2617" data-mp="3p5ngyfw8y5rv">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-2618" data-mp="3k3g13f8aq5k9">→</span>
                    <span data-id="element-2619" data-mp="2dox6gkksirsg">How to stack peptides for synergistic effects</span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-2620" data-mp="28ot19qpc1jhj">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-2621" data-mp="28rf7nn0hlsy6">→</span>
                    <span data-id="element-2622" data-mp="10r6oez1dsvin">
                      How to optimize protocols based on real-time feedback
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-2623" data-mp="qry12p10uf59">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-2624" data-mp="qr67tkuwcti5">→</span>
                    <span data-id="element-2625" data-mp="vpptrl9hq5ai">
                      How to create transformations for yourself and others
                    </span>
                  </p>
                </div>

                <p className="text-xl text-emerald-400 font-bold" data-id="element-2626" data-mp="lt4lmr26ce2g">
                  This is what allows you to operate at a level most
                  practitioners will never reach.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Benefit 4: State of the Union */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="relative" data-id="element-2627" data-mp="3u84tfzdzkpyq">
            <div className="absolute inset-0 bg-gradient-radial from-blue-600/10 via-transparent to-transparent blur-2xl" data-id="element-2628" data-mp="3bkdvvk1k06ry" />

            <div className="relative bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-5 sm:p-8 md:p-10 rounded-lg" data-id="element-2629" data-mp="3pkwww77lqckm">
              <div className="flex items-start gap-4 mb-6" data-id="element-2630" data-mp="3kmvdfl69kb8m">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-2631" data-mp="2duxe23vidze">
                  <TrendingUp className="w-6 h-6 text-black" data-id="element-2632" data-mp="3uigl9zy0k5qa" />
                </div>
                <div className="flex-1" data-id="element-2633" data-mp="c3nxa5unl4nt">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2634" data-mp="c6tl7hrjq0hy">
                    <span className="text-green-400 text-2xl" data-id="element-2635" data-mp="76fpmz74sj0t">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-blue-400" data-id="element-2636" data-mp="m2dhi5a0v178">
                      THE STATE OF THE UNION: WHAT'S REALLY HAPPENING NOW
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-2637" data-mp="2akq1y5xcbino">
                <p data-id="element-2638" data-mp="2fk2p6rs4irqs">
                  Every month, Brenden and Ali will give you a STRATEGIC
                  BRIEFING about peptides, biohacking, healing or performance
                  enhancement.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-blue-500/30" data-id="element-2639" data-mp="2pdjkk07wsewk">
                  <p data-id="element-2640" data-mp="1rprlf0paj3mc">What's working in the real world.</p>
                  <p data-id="element-2641" data-mp="1wm895tskvfyw">What new research has emerged.</p>
                  <p data-id="element-2642" data-mp="1wounqddvyvqm">What misinformation is being spread.</p>
                  <p data-id="element-2643" data-mp="21lbezggdtq7x">Which vendors are legitimate and which are bunk.</p>
                  <p data-id="element-2644" data-mp="26lh8z3a4jot6">What trends to pay attention to and what to ignore.</p>
                </div>

                <div className="bg-blue-950/30 p-6 rounded-lg space-y-3" data-id="element-2645" data-mp="3d1t1oou6gehd">
                  <p className="text-xl text-blue-400 font-bold" data-id="element-2646" data-mp="3i0cjqe5nh64g">
                    This is your GROUNDED GUIDANCE.
                  </p>
                  <p className="text-stone-100" data-id="element-2647" data-mp="16bzeb9bscplb">
                    Your compass in a sea of noise.
                  </p>
                  <p className="text-stone-100" data-id="element-2648" data-mp="11eps02jp0u1p">
                    Your protection against the frauds, the liars, and the
                    grifters.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 5: Elite Secrets */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="relative" data-id="element-2649" data-mp="mqctaxt80x3i">
            <div className="absolute inset-0 bg-gradient-radial from-amber-600/10 via-transparent to-transparent blur-2xl" data-id="element-2650" data-mp="8qk4x9562syp" />

            <div className="relative bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-5 sm:p-8 md:p-10 rounded-lg" data-id="element-2651" data-mp="280a6u7by3ezu">
              <div className="flex items-start gap-4 mb-6" data-id="element-2652" data-mp="1dklbijjq9ra">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center" data-id="element-2653" data-mp="1c1860t6setq">
                  <Lock className="w-6 h-6 text-black" data-id="element-2654" data-mp="3nlc880g7f30t" />
                </div>
                <div className="flex-1" data-id="element-2655" data-mp="3nj7tsokoilma">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2656" data-mp="3sjvphnjurqmu">
                    <span className="text-green-400 text-2xl" data-id="element-2657" data-mp="3si2i261f12ds">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-amber-400" data-id="element-2658" data-mp="k551fl2hspnt">
                      ELITE, HIGH-LEVEL SECRETS YOU WON'T FIND ANYWHERE ELSE
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-2659" data-mp="f58y7o1md29b">
                <p data-id="element-2660" data-mp="2uwws1alc6enw">
                  There are things Brenden and Ali will share during these
                  sessions that you will NEVER find outside of this membership.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-amber-500/30" data-id="element-2661" data-mp="1hy4rx0mhuvmv">
                  <p data-id="element-2662" data-mp="1hzxxecldf9ta">
                    Insights that would fundamentally change how you think about
                    biology.
                  </p>
                  <p data-id="element-2663" data-mp="1qxrsxlbb9zfe">
                    Secrets that sound too good to be true... until you see the
                    science behind them.
                  </p>
                  <p data-id="element-2664" data-mp="z7dk43i5ndal">
                    Strategies that are so effective they border on unfair
                    advantages.
                  </p>
                </div>

                <p className="italic text-stone-400" data-id="element-2665" data-mp="u6fsl0b996wf">
                  I can't reveal what these are on this page.
                </p>

                <div className="bg-amber-950/30 p-6 rounded-lg" data-id="element-2666" data-mp="138p1jlqvks7p">
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-2667" data-mp="137dwzjplvgfj">
                    But I can tell you this:
                  </p>
                  <div className="space-y-2" data-id="element-2668" data-mp="biubfmv70yhz">
                    <p data-id="element-2669" data-mp="bhsq8pkgqvmj">
                      If you're the kind of person who values being 10-20 years
                      ahead of the mainstream...
                    </p>
                    <p data-id="element-2670" data-mp="15cpsfu1u8x24">
                      If you want access to knowledge that gives you a REAL
                      edge...
                    </p>
                    <p className="text-xl text-amber-400 font-bold" data-id="element-2671" data-mp="1eccv0j6b08wr">
                      Then this alone is worth the price of admission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 6: Monthly Q&A */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="relative" data-id="element-2672" data-mp="1t0z8efcdeqlc">
            <div className="absolute inset-0 bg-gradient-radial from-rose-600/10 via-transparent to-transparent blur-2xl" data-id="element-2673" data-mp="271a1zq52eja7" />

            <div className="relative bg-gradient-to-br from-rose-950/40 to-black border-2 border-rose-500/50 p-5 sm:p-8 md:p-10 rounded-lg" data-id="element-2674" data-mp="2h5h01j7nkzje">
              <div className="flex items-start gap-4 mb-6" data-id="element-2675" data-mp="2c4sxc0a2au9c">
                <div className="flex-shrink-0 w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center" data-id="element-2676" data-mp="2769cy4b49qj3">
                  <MessageCircle className="w-6 h-6 text-black" data-id="element-2677" data-mp="236r1s093kttg" />
                </div>
                <div className="flex-1" data-id="element-2678" data-mp="2zun9sgqgygt2">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2679" data-mp="2zxsyhuv6rgo9">
                    <span className="text-green-400 text-2xl" data-id="element-2680" data-mp="2uxwuvwqehr98">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-rose-400" data-id="element-2681" data-mp="2q5dhazrpqw4f">
                      MONTHLY Q&A SESSIONS WHERE YOU CAN GET YOUR OWN QUESTIONS
                      ANSWERED
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-2682" data-mp="3dvhn9dsdj7yl">
                <p className="text-xl text-rose-400 font-bold" data-id="element-2683" data-mp="3dtn1jx9a3btg">
                  This is where PSI PRO becomes PERSONAL.
                </p>

                <p data-id="element-2684" data-mp="1489gcw9wiiin">You're not just watching pre-recorded content.</p>

                <p className="text-stone-100 font-semibold" data-id="element-2685" data-mp="z9zj8g4m97ue">
                  You're getting DIRECT ACCESS to Brenden and Ali.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-rose-500/30" data-id="element-2686" data-mp="ucpockygk3z6">
                  <p data-id="element-2687" data-mp="1rxvfxku6qzr6">You can submit your questions in advance.</p>
                  <p data-id="element-2688" data-mp="1n02awv4mcbsh">You can ask them live during the sessions.</p>
                  <p className="text-stone-100 font-semibold" data-id="element-2689" data-mp="1n1wwmbnps7xm">
                    You can get answers about YOUR specific situation.
                  </p>
                </div>

                <div className="bg-rose-950/30 p-6 rounded-lg space-y-3" data-id="element-2690" data-mp="1d97jlccimm06">
                  <p className="text-stone-100 font-bold" data-id="element-2691" data-mp="2apo6z9pp2aw9">YOUR protocols.</p>
                  <p className="text-stone-100 font-bold" data-id="element-2692" data-mp="25yy3y34qqk4q">YOUR results.</p>
                  <p className="text-stone-100 font-bold" data-id="element-2693" data-mp="25wlfd74h9bip">YOUR challenges.</p>
                </div>

                <p className="text-xl text-rose-400 font-bold" data-id="element-2694" data-mp="2dc730nds5s3l">
                  This is similar to the kind of access that private clients pay
                  $1,500/hour for.
                </p>

                <p className="text-stone-100 font-semibold" data-id="element-2695" data-mp="2nad4j3vocw7x">
                  You're getting it as part of your membership.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>;
}